import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { TrendingUp, Clock, ChevronLeft, ChevronRight, Zap } from 'lucide-react';
import { useSocket } from '../contexts/SocketContext';
import { formatCurrency } from '../utils/formatting';
import { apiUrl } from '../config/api';
import { unwrapApiData } from '../utils/apiResponse';

interface LiveBid {
  id: string | number;
  auctionId: string | number;
  auctionTitle: string;
  bidderName: string;
  amount: number;
  timestamp: Date;
}

const LiveBidsTicker: React.FC = () => {
  const { t } = useTranslation('common');
  const { socket } = useSocket();
  const [bids, setBids] = useState<LiveBid[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRecentBids = useCallback(async () => {
    try {
      const url = apiUrl('/auctions/recent-bids');
      // console.log('LiveBidsTicker: Fetching from', url); // تم تعطيله
      const res = await axios.get(url);
      
      let bidsData: any[] = [];
      const unwrapped = unwrapApiData<any>(res.data);
      
      if (Array.isArray(unwrapped)) {
        bidsData = unwrapped;
      } else if (unwrapped && Array.isArray(unwrapped.bids)) {
        bidsData = unwrapped.bids;
      } else if (res.data && Array.isArray(res.data.bids)) {
        bidsData = res.data.bids;
      }

      if (bidsData.length > 0) {
        setBids(bidsData.map(b => ({
          ...b,
          timestamp: new Date(b.timestamp)
        })));
      } else {
        // console.log('LiveBidsTicker: No bids returned from API'); // تم تعطيله
      }
    } catch (err) {
      console.error('LiveBidsTicker: API Error', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecentBids();
  }, [fetchRecentBids]);

  useEffect(() => {
    if (!socket) return;

    const handleNewBid = (data: any) => {
      const newBid: LiveBid = {
        id: `socket-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        auctionId: data.auctionId,
        auctionTitle: data.auction?.title || '...',
        bidderName: data.bid?.bidder?.name || t('card.user'),
        amount: data.bid?.amount,
        timestamp: new Date(data.bid?.createdAt || Date.now())
      };

      setBids(prev => {
        if (prev.some(b => b.auctionId === newBid.auctionId && b.amount === newBid.amount)) {
          return prev;
        }
        return [newBid, ...prev].slice(0, 15); // زيادة العدد قليلاً للأفقي
      });
    };

    socket.on('new_bid', handleNewBid);
    return () => {
      socket.off('new_bid', handleNewBid);
    };
  }, [socket, t]);

  // Always show the ticker row if loading is done, even if empty, to avoid UI jumps
  // We will show a placeholder instead of just returning null.
  if (!loading && bids.length === 0) {
    // Optional: could return a placeholder here or just keep the header visible.
    // For now, let's keep it visible so we know it's working.
  }

  return (
    <div className="relative group/ticker">
      <div className="flex items-center gap-2 lg:gap-3 bg-white/40 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-full h-9 lg:h-11 p-1 lg:p-1.5 shadow-sm overflow-hidden">
        {/* Label Section */}
        <div className="shrink-0 flex items-center gap-2 px-2 lg:px-3 py-1 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full shadow-md z-10">
          <div className="relative">
            <TrendingUp className="w-3.5 h-3.5 text-white" />
            <span className="absolute -top-1 -right-1 flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-200 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white"></span>
            </span>
          </div>
          <span className="text-[10px] font-black text-white uppercase tracking-wider whitespace-nowrap">
            {t('home.liveBids.title')}
          </span>
        </div>

        {/* Ticker Content */}
        <div className="flex-1 overflow-hidden whitespace-nowrap flex items-center">
          <div className="flex items-center gap-2 py-0.5 animate-marquee min-w-max">
            {loading ? (
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="h-6 w-32 bg-slate-200/50 dark:bg-slate-700/50 rounded-full animate-pulse" />
                ))}
              </div>
            ) : (
              <>
                {bids.map((bid) => (
                  <Link
                    key={bid.id}
                    to={`/auction/${bid.auctionId}`}
                    className="shrink-0 group flex items-center gap-2 bg-white/80 dark:bg-slate-800/80 border border-slate-100 dark:border-slate-700/80 px-3 py-1 rounded-full shadow-sm hover:shadow-md hover:border-emerald-500/30 hover:scale-[1.02] active:scale-95 transition-all duration-300 mx-1"
                  >
                    <Zap className="w-3 h-3 text-amber-500 group-hover:animate-bounce" />
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 max-w-[60px] truncate">
                        {bid.bidderName}
                      </span>
                      <div className="w-px h-2.5 bg-slate-200 dark:bg-slate-700" />
                      <span className="text-[11px] font-black text-slate-900 dark:text-white">
                        {formatCurrency(bid.amount)}
                      </span>
                      <div className="w-px h-2.5 bg-slate-200 dark:bg-slate-700" />
                      <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500 max-w-[100px] truncate italic group-hover:text-emerald-500 transition-colors">
                        {bid.auctionTitle}
                      </span>
                    </div>
                  </Link>
                ))}
                {/* Duplicate for seamless infinite loop */}
                {bids.map((bid) => (
                  <Link
                    key={`dup-${bid.id}`}
                    to={`/auction/${bid.auctionId}`}
                    className="shrink-0 group flex items-center gap-2 bg-white/80 dark:bg-slate-800/80 border border-slate-100 dark:border-slate-700/80 px-3 py-1 rounded-full shadow-sm hover:shadow-md hover:border-emerald-500/30 hover:scale-[1.02] active:scale-95 transition-all duration-300 mx-1"
                  >
                    <Zap className="w-3 h-3 text-amber-500 group-hover:animate-bounce" />
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 max-w-[60px] truncate">
                        {bid.bidderName}
                      </span>
                      <div className="w-px h-2.5 bg-slate-200 dark:bg-slate-700" />
                      <span className="text-[11px] font-black text-slate-900 dark:text-white">
                        {formatCurrency(bid.amount)}
                      </span>
                      <div className="w-px h-2.5 bg-slate-200 dark:bg-slate-700" />
                      <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500 max-w-[100px] truncate italic group-hover:text-emerald-500 transition-colors">
                        {bid.auctionTitle}
                      </span>
                    </div>
                  </Link>
                ))}
              </>
            )}
          </div>
        </div>

        {/* Scroll Hints (visual only) */}
        <div className="shrink-0 flex items-center gap-1 text-slate-300 dark:text-slate-600 px-2">
          <ChevronRight className="w-4 h-4 animate-pulse" />
        </div>
      </div>
    </div>
  );
};

export default LiveBidsTicker;
